import { ReportedHourResponseSchema } from "./schemas"
import type { ReportedHourResponse } from "./schemas"
import {
  convertDecimalHoursToHoursAndMinutes,
  convertHHmmToDate,
  convertHoursAndMinutesToDecimalHours,
  getDurationInHoursAndMinutes,
} from "../utils"

type ReportedHourProject = ReportedHourResponse["project"] & {
  displayTitle: string
}

export type ReportedHourViewModel = Omit<ReportedHourResponse, "project"> & {
  durationInDecimalHours: number
  durationInHoursAndMinutes: { hours: number; minutes: number }
  isReportedManually: boolean
  isReportedByInterval: boolean
  isReportedByTimer: boolean
  project: ReportedHourProject | null
  isReadyForCheck: boolean
  isReadyForApproval: boolean
  isApproved: boolean
  isReadyForCheckEnabled: boolean
  isReadyForApprovalEnabled: boolean
  isApprovedEnabled: boolean
}

const shouldEnableReadyForCheck = (hourReport: ReportedHourResponse): boolean => {
  if (hourReport.status === "approved" || hourReport.status === "checked") return false
  return hourReport.canMarkReadyForCheck
}

const shouldEnableReadyForApproval = (hourReport: ReportedHourResponse): boolean => {
  if (hourReport.status === "approved") return false
  return hourReport.canMarkReadyForApproval
}

const shouldEnableApproved = (hourReport: ReportedHourResponse): boolean => {
  return hourReport.canMarkApproved
}

const isReadyForCheck = (status: ReportedHourResponse["status"]) => {
  return status === "submitted" || status === "checked" || status === "approved"
}

const isReadyForApproval = (status: ReportedHourResponse["status"]) => {
  return status === "checked" || status === "approved"
}

const isApproved = (status: ReportedHourResponse["status"]) => {
  return status === "approved"
}

const makeReportedHourProject = (project: ReportedHourResponse["project"]): ReportedHourProject | null => {
  if (!project) return null

  return {
    ...project,
    displayTitle: `${project.projectNumber} ${project.title}`,
  }
}

export const makeReportedHourViewModel = (hourReport: ReportedHourResponse): ReportedHourViewModel => {
  const parsed = ReportedHourResponseSchema.parse(hourReport)

  return {
    ...parsed,
    durationInDecimalHours: parsed.hours,
    durationInHoursAndMinutes: convertDecimalHoursToHoursAndMinutes(parsed.hours),
    isReportedManually: parsed.method === "manual",
    isReportedByInterval: parsed.method === "interval",
    isReportedByTimer: parsed.method === "timer",
    project: makeReportedHourProject(parsed.project),
    isReadyForCheck: isReadyForCheck(parsed.status),
    isReadyForApproval: isReadyForApproval(parsed.status),
    isApproved: isApproved(parsed.status),
    isReadyForCheckEnabled: shouldEnableReadyForCheck(parsed),
    isReadyForApprovalEnabled: shouldEnableReadyForApproval(parsed),
    isApprovedEnabled: shouldEnableApproved(parsed),
  }
}

export type ReportedHourOptimisticUpdate = Partial<
  Pick<ReportedHourResponse, "description" | "projectId" | "taskId" | "hours" | "startTime" | "endTime" | "status">
>

export const applyReportedHourOptimisticUpdate = (
  hourReport: ReportedHourViewModel,
  update: ReportedHourOptimisticUpdate
): ReportedHourViewModel => {
  const updatedHourReport = { ...hourReport, ...update }

  if (update.projectId !== undefined && update.projectId !== hourReport.projectId) {
    updatedHourReport.taskId = null
    updatedHourReport.task = null
  }

  if (update.startTime !== undefined || update.endTime !== undefined) {
    const reportDate = new Date(updatedHourReport.date)
    const startTime = updatedHourReport.startTime
      ? convertHHmmToDate({ HHmm: updatedHourReport.startTime, date: reportDate })
      : undefined
    const endTime = updatedHourReport.endTime
      ? convertHHmmToDate({ HHmm: updatedHourReport.endTime, date: reportDate })
      : undefined
    const duration = getDurationInHoursAndMinutes(startTime, endTime)

    updatedHourReport.hours = convertHoursAndMinutesToDecimalHours(duration.hours, duration.minutes)
  }

  return makeReportedHourViewModel(updatedHourReport)
}
